// Support-csapatok — a belső szervezeti egységek (workforce/support-teams) + tagságaik + a hozzájuk kötött
// szolgáltatások listája az ÉLŐ adatból, a KÖZÖS DataTable-re építve (a clients.index / employees a referencia-
// minta). Szerver-oldali kereső (név/e-mail/leírás/tag-név/szolgáltatás, 300 ms debounce), szerver-rendezés (név,
// locale-collation — csak az irány váltható), végtelen görgetés (100/lap; a csapatok száma kicsi → egy lap).
//
// Legacy-parity (régi CRM „Ügyfélszolgálatok" lista): név(hu/en) + szolgáltatások + tagok. Az új designban a NÉV a
// fő sor + az E-MAIL halvány másodlagos sorban ugyanabban az oszlopban (nincs külön oszlop) · Szolgáltatások (a
// csapathoz kötött billing-szolgáltatások — a ticket-leadásnál releváns, 4+ szolgáltatásnál legördülő) · Tagok
// (profilkép + név, 4+ tagnál legördülő).
import { type SupportTeamRowSchema } from '@atlas/contracts';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { DeleteRowAction, EditRowAction, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { SupportTeamFormSheet } from '@/components/support-teams/support-team-form-sheet';
import { Text } from '@/components/ui/text';
import { supportTeamKeys } from '@/features/support-teams/api';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// Deep-link: `?edit=<id>` → a szerkesztő-sheet a megadott support-csapatra nyílik (a command palette „2. rungja").
const supportTeamsSearchSchema = z.object({
  edit: z.string().optional(),
  q: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/support-teams')({
  validateSearch: (search) => supportTeamsSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: SupportTeamsRoute,
});

// Route-wrapper: a deep-link search-paramot itt olvassuk + a param-törlést itt navigáljuk (a lista-oldal router-hook-mentes).
function SupportTeamsRoute(): JSX.Element {
  const { edit } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/support-teams',
      search: (prev) => ({ ...prev, edit: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <SupportTeamsPage deepLinkEditId={edit} onClearDeepLink={clearDeepLink} />;
}

type SupportTeamRow = z.infer<typeof SupportTeamRowSchema>;
type Member = SupportTeamRow['members'][number];
type SupportService = SupportTeamRow['services'][number];

const PAGE_SIZE = 100;

export interface SupportTeamsPageProps {
  /** Deep-link: a `?edit=` paraméterből érkező csapat-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

export function SupportTeamsPage({
  deepLinkEditId,
  onClearDeepLink,
}: SupportTeamsPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('support_teams.view');
  const canManage = can('support_teams.manage');
  const canDelete = can('support_teams.delete');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SupportTeamRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SupportTeamRow | null>(null);

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    order: 'asc',
  });
  const q = urlFilters.q;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // A rendezés-kulcs fixen `name` (szerver-oldal, locale-collation) — csak az irány váltható.

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: support_teams.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // A rendezés + a tag-nevek LOCALE-tudatosak (HU→vezetéknév elöl; EN→keresztnév elöl) → a nyelv a query-kulcs
  // része, hogy nyelvváltáskor újratöltsön.
  const lang: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  const filters = { q, order, lang };
  const listKey = supportTeamKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.supportTeams.query({
        query: { q: q || undefined, order, lang, limit: PAGE_SIZE, offset: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.supportTeams : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` csapatra nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<SupportTeamRow>({
    id: deepLinkEditId,
    rows,
    getId: (team) => team.id,
    onOpen: (team) => setEditTarget(team),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));

  const hasFilters = q !== '';
  const toggleOrder = useCallback(
    () => setFilters({ order: order === 'asc' ? 'desc' : 'asc' }),
    [order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés — optimista lista-sor eltávolítás + rollback (a billing/employees-minta). A BFF a törlés-guardot
  // (aktív ticket → 409) is elvégzi; 409 → conflict-üzenet („használatban").
  type ListPage = Awaited<ReturnType<typeof api.supportTeams.query>>;
  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.supportTeamDelete.mutation({ params: { id } }),
    invalidateKeys: [supportTeamKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        const id = deleteTarget?.id;
        if (!old || !id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    supportTeams: p.body.supportTeams.filter((it) => it.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:supportTeams.deleted'),
      conflict: t('toast:supportTeams.inUse'),
      forbidden: t('toast:supportTeams.forbidden'),
      validation: t('toast:supportTeams.validation'),
      error: t('toast:supportTeams.deleteError'),
    },
  });
  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
    }
  }, [deleteTarget, removeMutation]);

  const emDash = <span className="text-muted-foreground">—</span>;

  // Egy tag megjelenítendő neve (locale-sorrend, mint a munkatársak-oldalon).
  const memberName = useCallback(
    (m: Member): string =>
      formatName(
        { givenName: m.givenName, familyName: m.familyName, displayName: m.displayName },
        i18n.language,
      ),
    [i18n.language],
  );

  const columns: DataTableColumn<SupportTeamRow>[] = [
    {
      // 1. Csapat — a NÉV a fő sor (itemTitle), az E-MAIL halvány másodlagos sorban ugyanabban az oszlopban
      // (nincs külön oszlop — user-döntés: a leírás helyett a kapcsolattartó-cím). A régi lista 1. oszlopának utódja.
      id: 'name',
      header: t('supportTeams.columns.team'),
      sortable: true,
      cardLabel: t('supportTeams.columns.team'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      cell: (team) => (
        <div className="min-w-0 space-y-0.5">
          <Text variant="itemTitle" as="div" className="truncate">
            {team.name}
          </Text>
          {team.email && (
            <Text tone="tertiary" as="div" className="truncate">
              {team.email}
            </Text>
          )}
        </div>
      ),
    },
    {
      // 2. Szolgáltatások — a csapathoz kötött billing/services (a legacy „Szolgáltatások" oszlop utódja;
      // a ticket-leadásnál releváns, mely szolgáltatások tartoznak a csapathoz). Csak a név; 4+ szolgáltatásnál
      // legördülő (ExpandableCell, mint a tagoknál).
      id: 'services',
      header: t('supportTeams.columns.services'),
      cardLabel: t('supportTeams.columns.services'),
      width: 'minmax(0,2fr)',
      multiline: true,
      // Mobil-kártya: REJTVE — másodlagos lista-cella (ExpandableCell); a kártyán a tagok viszik a subtitle-t.
      card: 'hidden',
      cell: (team) =>
        team.services.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<SupportService>
            items={team.services}
            getKey={(s) => s.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('supportTeams.moreServices', { count: n })}
            triggerAriaLabel={t('supportTeams.moreServicesAria')}
            renderItem={(s) => <span className="block min-w-0 truncate">{s.name}</span>}
          />
        ),
    },
    {
      // 3. Tagok — a workforce/support-team-members (belső identity-k): profilkép + név; 4+ tagnál legördülő
      // (ExpandableCell, mint a fiókok/munkatársak oldalon).
      id: 'members',
      header: t('supportTeams.columns.members'),
      cardLabel: t('supportTeams.columns.members'),
      width: 'minmax(0,2fr)',
      multiline: true,
      // Mobil-kártya: a 2. sor (subtitle) — a csapattagok a kulcs másodlagos infó.
      card: 'subtitle',
      cell: (team) =>
        team.members.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell
            items={team.members}
            getKey={(m) => m.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('supportTeams.moreMembers', { count: n })}
            triggerAriaLabel={t('supportTeams.moreMembersAria')}
            renderItem={(m) => {
              const name = memberName(m);
              return (
                <PersonChip
                  name={name}
                  avatarUrl={m.avatarUrl}
                  accentColor={m.teamColor}
                  size="sm"
                  className="gap-1.5"
                />
              );
            }}
          />
        ),
    },
  ];

  return (
    <AppShell
      toolbar={
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('supportTeams.loading')
              : !isError && !infinite.isLoading
                ? t('supportTeams.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            <div className="hidden md:block">
              <ListNewButton
                label={t('supportTeams.new')}
                onClick={() => setCreateOpen(true)}
                enabled={canManage}
                disabledTooltip={t('common.noCreatePermission')}
              />
            </div>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<SupportTeamRow>
          columns={columns}
          rows={rows}
          getRowId={(team) => team.id}
          sort="name"
          order={order}
          onSort={toggleOrder}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('supportTeams.noMatches')}
                description={t('supportTeams.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('supportTeams.empty')}
                description={t('supportTeams.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('supportTeams.actions')}
          actionsColWidth={canManage && canDelete ? '7rem' : canManage || canDelete ? '4rem' : '0'}
          rowActions={
            canManage || canDelete
              ? (team) => (
                  <RowActions>
                    {canManage && (
                      <EditRowAction
                        label={t('supportTeams.edit')}
                        onClick={() => setEditTarget(team)}
                      />
                    )}
                    {canDelete && (
                      <DeleteRowAction
                        label={t('supportTeams.delete')}
                        onClick={() => setDeleteTarget(team)}
                      />
                    )}
                  </RowActions>
                )
              : undefined
          }
        />
      </div>

      <SupportTeamFormSheet open={createOpen} onOpenChange={setCreateOpen} team={null} />
      <SupportTeamFormSheet
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
          }
        }}
        team={editTarget}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('supportTeams.deleteTitle')}
        description={t('supportTeams.deleteDesc', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('supportTeams.delete')}
        cancelLabel={t('supportTeams.cancel')}
        pendingLabel={t('supportTeams.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
